export const TENANT_STAFF_ROLES = [
  "organization_admin",
  "facility_manager",
  "staff",
] as const;

export type TenantStaffRole = (typeof TENANT_STAFF_ROLES)[number];

export const STAFF_PERMISSIONS = [
  "staff.manage",
  "role.manage",
  "member.manage",
  "course.manage",
  "inventory.manage",
  "billing.manage",
  "refund.execute",
  "data.export",
  "notification.publish",
  "bulk_operation.execute",
  "inquiry.manage",
  "inquiry.delete",
] as const;

export type StaffPermission = (typeof STAFF_PERMISSIONS)[number];

export type StaffRoleDefinition = {
  label: string;
  description: string;
  fixedPermissions: readonly StaffPermission[];
  grantablePermissions: readonly StaffPermission[];
};

export const STAFF_PERMISSION_DEFINITIONS: Record<
  StaffPermission,
  { label: string; description: string }
> = {
  "staff.manage": {
    label: "スタッフ管理",
    description: "スタッフの招待、基本情報の編集、利用停止",
  },
  "role.manage": {
    label: "権限管理",
    description: "スタッフの役割・追加操作・既存スタッフのアクセス範囲の変更",
  },
  "member.manage": {
    label: "会員管理",
    description: "会員・受講者情報の登録、編集、削除と会員状態の管理",
  },
  "course.manage": {
    label: "コース管理",
    description:
      "コース、クラス、レッスン枠の変更と、全施設担当者による講座レベル設定",
  },
  "inventory.manage": {
    label: "在庫管理",
    description:
      "施設別在庫の調整と、全施設担当者による商品・カテゴリ・設備タグ設定",
  },
  "billing.manage": {
    label: "請求・決済管理",
    description: "請求確定、再決済、入金消込などの決済運用",
  },
  "refund.execute": {
    label: "返金実行",
    description: "決済済み注文や決済履歴に対する返金の実行",
  },
  "data.export": {
    label: "データ出力",
    description: "顧客・売上・出欠情報のCSV出力",
  },
  "notification.publish": {
    label: "お知らせ配信",
    description: "メール・お知らせの公開と一斉配信",
  },
  "bulk_operation.execute": {
    label: "一括操作",
    description: "データの一括更新、一括削除、一括確定",
  },
  "inquiry.manage": {
    label: "お問い合わせ対応",
    description: "お問い合わせの確認、担当引受、対応状況の更新",
  },
  "inquiry.delete": {
    label: "お問い合わせ削除",
    description: "対応済みのお問い合わせの削除",
  },
};

export const STAFF_ROLE_DEFINITIONS: Record<
  TenantStaffRole,
  StaffRoleDefinition
> = {
  organization_admin: {
    label: "組織管理者",
    description: "組織全体の設定、施設、スタッフ、権限を管理します。",
    fixedPermissions: STAFF_PERMISSIONS,
    grantablePermissions: [],
  },
  facility_manager: {
    label: "施設管理者",
    description: "担当施設の設定とスタッフの日常業務を管理します。",
    fixedPermissions: [
      "staff.manage",
      "member.manage",
      "course.manage",
      "inventory.manage",
      "notification.publish",
      "inquiry.manage",
    ],
    grantablePermissions: [
      "billing.manage",
      "refund.execute",
      "data.export",
      "bulk_operation.execute",
    ],
  },
  staff: {
    label: "一般スタッフ",
    description: "担当施設の日常業務を行います。",
    fixedPermissions: ["inquiry.manage"],
    grantablePermissions: ["notification.publish"],
  },
};

export function isTenantStaffRole(role: unknown): role is TenantStaffRole {
  return TENANT_STAFF_ROLES.includes(role as TenantStaffRole);
}

export function isStaffPermission(value: unknown): value is StaffPermission {
  return STAFF_PERMISSIONS.includes(value as StaffPermission);
}

export function getStaffRoleLabel(role: TenantStaffRole): string {
  return STAFF_ROLE_DEFINITIONS[role].label;
}

const filterGrantablePermissions = (
  role: TenantStaffRole,
  permissions: readonly StaffPermission[]
): StaffPermission[] => {
  const grantable = STAFF_ROLE_DEFINITIONS[role].grantablePermissions;
  return [...new Set(permissions)].filter((permission) =>
    grantable.includes(permission)
  );
};

/** APIに保存する追加付与分だけに正規化する。 */
export function normalizePermissionGrants(
  role: TenantStaffRole,
  permissions: readonly StaffPermission[]
): StaffPermission[] {
  const grantable = STAFF_ROLE_DEFINITIONS[role].grantablePermissions;
  const normalized = filterGrantablePermissions(role, permissions);

  // 返金操作は請求情報へのアクセスと組み合わせて初めて実行できる。
  // UIの操作順や古い保存値にかかわらず、無効な「返金だけ」の付与を送らない。
  if (
    normalized.includes("refund.execute") &&
    grantable.includes("billing.manage") &&
    !normalized.includes("billing.manage")
  ) {
    const refundIndex = normalized.indexOf("refund.execute");
    normalized.splice(refundIndex, 0, "billing.manage");
  }

  return normalized;
}

/**
 * 権限管理を持たない操作ユーザーのリクエストから、ロール関連フィールドを除外する。
 * disabledなフォーム値を誤ってAPIへ送らないための共通payload生成処理。
 */
export function buildRolePermissionGrantPayload(
  canManageRoles: boolean,
  role: TenantStaffRole,
  permissions: readonly StaffPermission[]
): { role?: TenantStaffRole; permissions?: StaffPermission[] } {
  if (!canManageRoles) return {};
  return {
    role,
    permissions: normalizePermissionGrants(role, permissions),
  };
}

export function buildStaffVerificationPayload(
  canManageRoles: boolean,
  targetRole: TenantStaffRole | undefined,
  isVerified: boolean
): { isVerified?: boolean } {
  if (targetRole === "organization_admin" && !canManageRoles) return {};
  return { isVerified };
}

export function normalizeStaffFacilityAccess(
  role: TenantStaffRole,
  facilityScope: "GLOBAL" | "FACILITY_SPECIFIC",
  facilityIds: readonly string[] = []
): {
  facilityScope: "GLOBAL" | "FACILITY_SPECIFIC";
  facilityIds?: string[];
} {
  if (role === "organization_admin" || facilityScope === "GLOBAL") {
    return { facilityScope: "GLOBAL" };
  }
  return {
    facilityScope: "FACILITY_SPECIFIC",
    facilityIds: [...new Set(facilityIds)],
  };
}

export function shouldBlockOrganizationAdminRoleChange(
  originalRole: TenantStaffRole | undefined,
  originalIsVerified: boolean | undefined,
  nextRole: TenantStaffRole,
  nextIsVerified: boolean,
  countChecked: boolean,
  isLastOrganizationAdmin: boolean
): boolean {
  const removesOrganizationAdminAccess =
    originalRole === "organization_admin" &&
    originalIsVerified === true &&
    (nextRole !== "organization_admin" || !nextIsVerified);
  return (
    removesOrganizationAdminAccess && (!countChecked || isLastOrganizationAdmin)
  );
}

/** ロール固定権限と追加付与を合わせた実効権限を返す。 */
export function resolveEffectivePermissions(
  role: TenantStaffRole,
  permissionGrants: readonly StaffPermission[] = []
): StaffPermission[] {
  return [
    ...new Set([
      ...STAFF_ROLE_DEFINITIONS[role].fixedPermissions,
      // 既存ユーザーの実効アクセス判定では保存値を増やさない。
      // 返金だけの古い値を請求権限ありと誤認しないため、依存補完は保存時だけ行う。
      ...filterGrantablePermissions(role, permissionGrants),
    ]),
  ];
}

export function hasEffectivePermission(
  role: unknown,
  permission: StaffPermission,
  effectivePermissions?: readonly StaffPermission[],
  permissionGrants?: readonly StaffPermission[]
): boolean {
  if (!isTenantStaffRole(role)) return false;
  // 配列が返っている場合はAPIの権威値とし、欠落時だけロール既定へ戻す。
  if (effectivePermissions !== undefined) {
    return effectivePermissions.includes(permission);
  }
  return resolveEffectivePermissions(role, permissionGrants).includes(
    permission
  );
}
